import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { BACKEND_URL } from '../config/config';

export const ACCESS_TOKEN_STORAGE_KEY = 'auth_token';
export const REFRESH_TOKEN_STORAGE_KEY = 'auth_refresh_token';
export const AUTH_USER_STORAGE_KEY = 'auth_user';
export const AUTH_TOKEN_REFRESHED_EVENT = 'auth:token-refreshed';
export const AUTH_CLEARED_EVENT = 'auth:cleared';

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

const api = axios.create({
  baseURL: BACKEND_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const refreshApi = axios.create({
  baseURL: BACKEND_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const authRoutesWithoutRefresh = [
  '/auth/login',
  '/auth/signup',
  '/auth/refresh-token',
  '/auth/forgot-password',
  '/auth/verify-email',
  '/auth/verify-reset-otp',
  '/auth/resend-otp',
  '/auth/reset-password',
];

let refreshTokenRequest: Promise<string> | null = null;

const getStoredValue = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const setStoredValue = (key: string, value?: string | null) => {
  try {
    if (value) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  } catch (e) {}
};

const isAuthRouteWithoutRefresh = (url?: string) => {
  if (!url) return false;
  return authRoutesWithoutRefresh.some((route) => url.includes(route));
};

const dispatchTokenRefresh = (
  accessToken: string,
  refreshToken: string,
  user?: unknown,
) => {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(
    new CustomEvent(AUTH_TOKEN_REFRESHED_EVENT, {
      detail: {
        accessToken,
        refreshToken,
        user,
      },
    }),
  );
};

export function setAuthToken(token?: string | null) {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
}

export const clearStoredAuth = () => {
  setStoredValue(ACCESS_TOKEN_STORAGE_KEY, null);
  setStoredValue(REFRESH_TOKEN_STORAGE_KEY, null);
  setStoredValue(AUTH_USER_STORAGE_KEY, null);
  setStoredValue('user_country', null);
  setAuthToken(null);

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(AUTH_CLEARED_EVENT));
  }
};

const persistRefreshResponse = (data: any, fallbackRefreshToken: string) => {
  const accessToken = data?.accessToken || data?.token;
  const refreshToken =
    data?.refreshToken || data?.refresh_token || fallbackRefreshToken;

  if (!accessToken) {
    throw new Error('Refresh response did not include an access token');
  }

  setStoredValue(ACCESS_TOKEN_STORAGE_KEY, accessToken);
  setStoredValue(REFRESH_TOKEN_STORAGE_KEY, refreshToken);

  if (data?.user) {
    try {
      localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(data.user));
    } catch (e) {}
  }

  setAuthToken(accessToken);
  dispatchTokenRefresh(accessToken, refreshToken, data?.user);

  return accessToken;
};

const requestAccessTokenRefresh = async () => {
  const refreshToken = getStoredValue(REFRESH_TOKEN_STORAGE_KEY);

  if (!refreshToken) {
    throw new Error('Refresh token is missing');
  }

  const response = await refreshApi.post('/auth/refresh-token', {
    refreshToken,
  });

  return persistRefreshResponse(response.data, refreshToken);
};

const getFreshAccessToken = () => {
  if (!refreshTokenRequest) {
    refreshTokenRequest = requestAccessTokenRefresh().finally(() => {
      refreshTokenRequest = null;
    });
  }

  return refreshTokenRequest;
};

// Attach access token from localStorage (keeps it decoupled from React lifecycle)
api.interceptors.request.use(
  (config) => {
    try {
      const token = getStoredValue(ACCESS_TOKEN_STORAGE_KEY);
      if (token && config && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (e) {
      // ignore
    }
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;
    const status = error.response?.status;

    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      isAuthRouteWithoutRefresh(originalRequest.url)
    ) {
      return Promise.reject(error);
    }

    const refreshToken = getStoredValue(REFRESH_TOKEN_STORAGE_KEY);

    if (!refreshToken) {
      clearStoredAuth();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const accessToken = await getFreshAccessToken();

      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      }

      return api(originalRequest);
    } catch (refreshError) {
      clearStoredAuth();
      return Promise.reject(refreshError);
    }
  },
);

export default api;
